import type { QueryClient } from '@tanstack/react-query';
import { refreshProjectContextToKnowledge } from '../services/knowledgeIngestion';

export type ProjectKnowledgeSyncStatus = { state: 'syncing' | 'succeeded' | 'failed'; error?: string };

export function projectKnowledgeSyncKey(projectId: string): readonly ['projectKnowledgeSync', string] {
  return ['projectKnowledgeSync', projectId];
}

const RETRY_DELAYS_MS = [1000, 3000];

export async function syncProjectKnowledge(queryClient: QueryClient, projectId: string): Promise<void> {
  queryClient.setQueryData<ProjectKnowledgeSyncStatus>(projectKnowledgeSyncKey(projectId), { state: 'syncing' });
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt += 1) {
    try {
      await refreshProjectContextToKnowledge(projectId);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['knowledgeDocuments', projectId] }),
        queryClient.invalidateQueries({ queryKey: ['knowledgeSearch', projectId] }),
      ]);
      if (typeof window !== 'undefined') {
        try {
          window.localStorage.removeItem(`knowledge-query-session:${projectId}`);
        } catch {
          // Browser storage is optional; the active panel still receives the event.
        }
        window.dispatchEvent(new CustomEvent('project-knowledge-synced', { detail: { projectId } }));
      }
      queryClient.setQueryData<ProjectKnowledgeSyncStatus>(projectKnowledgeSyncKey(projectId), { state: 'succeeded' });
      return;
    } catch (error) {
      if (attempt < RETRY_DELAYS_MS.length) {
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS_MS[attempt]));
        continue;
      }
      const message = error instanceof Error ? error.message : String(error);
      queryClient.setQueryData<ProjectKnowledgeSyncStatus>(projectKnowledgeSyncKey(projectId), { state: 'failed', error: message });
      throw error;
    }
  }
}

export function scheduleProjectKnowledgeSync(queryClient: QueryClient, projectId: string): void {
  void syncProjectKnowledge(queryClient, projectId).catch((error: unknown) => {
    console.warn('Project Knowledge sync failed:', error);
  });
}
